import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Tooltip,
  Paper,
  Divider,
  Grid,
  List,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import MensagemCard from "./MensagemCard";
import BrindePorNivelSection from './BrindePorNivelSection';
import Loader from '../Loader';

import api from "../../../../services/api";

const MensagensTab = ({ onboardingStepId }) => {
  const [mensagens, setMensagens] = useState([]);
  const [mensagensNivelTemp, setMensagensNivelTemp] = useState({});
  const [brindes, setBrindes] = useState([]);
  const [, setBrindesPorNivel] = useState({});
  const [loading, setLoading] = useState(true);
  const [brindesPorNivelTemp, setBrindesPorNivelTemp] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [mensagemEditando, setMensagemEditando] = useState(null);
  const [form, setForm] = useState({
    mensagem: "",
    ordem: 1,
    nivel_id: "",
    pergunta_id: "",
    is_recorrencia: false,
  });
  const [niveisAbertos, setNiveisAbertos] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [niveisDisponiveis, setNiveisDisponiveis] = useState([]);
  const [novoNivelModalOpen, setNovoNivelModalOpen] = useState(false);
  const [formNovoNivel, setFormNovoNivel] = useState({
    nome: "",
    checkins: "",
    brinde_id: "",
    recorrencia_checkins: "",
    is_epic: false,
  });
  const [erroCheckinsDuplicado, setErroCheckinsDuplicado] = useState(false);
  const [perguntas, setPerguntas] = useState([]);
  // ...
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    subtitle: '',
    onConfirm: () => {}
  });

  const firstNonEpicCheckins = useMemo(() => {
    const values = niveisDisponiveis
      .filter((nivel) => !nivel.is_epic)
      .map((nivel) => Number(nivel.checkins))
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b);
    return values[0] ?? null;
  }, [niveisDisponiveis]);

  const epicCheckins = useMemo(() => {
    const epic = niveisDisponiveis.find((nivel) => nivel.is_epic);
    const value = Number(epic?.checkins);
    return Number.isFinite(value) ? value : null;
  }, [niveisDisponiveis]);

  const buildMensagemPayload = (data) => ({
    mensagem: data.mensagem,
    ordem: data.ordem,
    nivel_id: data.nivel_id,
    pergunta_id: data.pergunta_id ?? data.pergunta?.id ?? null,
    is_recorrencia: Boolean(data.is_recorrencia),
  });

  const normalizeOrdemValue = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const compareMensagensOrdem = (a, b) => {
    const ordemA = normalizeOrdemValue(a?.ordem);
    const ordemB = normalizeOrdemValue(b?.ordem);
    if (ordemA !== ordemB) return ordemA - ordemB;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
  };

  const buildOrdemUpdatesForGroup = (mensagensGrupo) => {
    if (!Array.isArray(mensagensGrupo) || mensagensGrupo.length === 0) return [];
    const sorted = [...mensagensGrupo].sort(compareMensagensOrdem);
    return sorted.reduce((acc, msg, index) => {
      const novaOrdem = index + 1;
      if (Number(msg.ordem) !== novaOrdem) {
        acc.push({ ...msg, ordem: novaOrdem });
      }
      return acc;
    }, []);
  };

  const applyOrdemUpdates = async (updates) => {
    if (!updates.length) return false;
    try {
      await Promise.all(
        updates.map((msg) => api.put(`/fidelidade/mensagens/${msg.id}`, buildMensagemPayload(msg)))
      );
      return true;
    } catch (err) {
      console.error("Erro ao reordenar mensagens:", err);
      showSnackbar("Erro ao reordenar mensagens.", "error");
      return false;
    }
  };

  const abrirConfirmacao = ({ title, subtitle, onConfirm }) => {
    setConfirmDialog({
      open: true,
      title,
      subtitle,
      onConfirm,
    });
  };

  const fetchNiveis = React.useCallback(async () => {
    const { data } = await api.get(`/fidelidade/niveis`);
    setNiveisDisponiveis(data);
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchMensagens = React.useCallback(async () => {
    const { data } = await api.get(`/fidelidade/mensagens`);
    const mensagensData = data || [];
    const mensagensSemNivel = mensagensData.map((mensagem) => {
      const { nivel, ...restante } = mensagem;
      return restante;
    });
    setMensagens(mensagensSemNivel);

    const agrupadas = {};

    niveisDisponiveis.forEach((nivel) => {
      agrupadas[nivel.checkins] = [];
    });

    mensagensData.forEach((msgOriginal, index) => {
      const mensagemAtual = mensagensSemNivel[index];
      const checkins = msgOriginal.nivel?.checkins ?? "sem_nivel";
      if (!agrupadas[checkins]) agrupadas[checkins] = [];
      agrupadas[checkins].push(mensagemAtual);
    });

    setMensagensNivelTemp(agrupadas);

    const niveisIds = new Set(niveisDisponiveis.map((nivel) => nivel.id));
    const nivelMissing = mensagensData.some((mensagem) => mensagem.nivel?.id && !niveisIds.has(mensagem.nivel.id));
    if (nivelMissing) {
      await fetchNiveis();
    }
  }, [niveisDisponiveis, fetchNiveis]);

  const fetchPerguntas = useCallback(async () => {
    const { data } = await api.get("/fidelidade/perguntas");
    setPerguntas(data || []);
  }, []);
  
  const fetchBrindes = React.useCallback(async () => {
    const { data } = await api.get(`/fidelidade/brindes`);
    setBrindes(data);
  }, []);
  
  const fetchBrindesPorNivel = React.useCallback(async () => {
    const { data } = await api.get(`/fidelidade/brindes-nivel`);
  
    if (data) {
      const mapa = {};
      data.forEach((item) => {
        if (item.brinde_id) {
          mapa[item.checkins] = item.brinde_id;
        }
      });
      setBrindesPorNivel(mapa);
      setBrindesPorNivelTemp(mapa);
    }
  }, []);  

  const salvarBrindePorNivel = async (nivel, brindeId) => {
    try {
      await api.post(
        "/fidelidade/brindes-nivel",
        {
          nivel,
          brinde_id: brindeId || null,
        }
      );
      showSnackbar("Brinde atualizado com sucesso");
    } catch (err) {
      console.error("Erro ao salvar brinde do nível:", err);
      const apiMessage = err?.response?.data?.error;
      showSnackbar(apiMessage || 'Erro ao salvar nível.', 'error');
    }
  };
  
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchNiveis(), fetchPerguntas()])
      .finally(() => setLoading(false));
  }, [fetchNiveis, fetchPerguntas]);
  
  useEffect(() => {
    if (niveisDisponiveis.length > 0) {
      setLoading(true);
      Promise.all([fetchMensagens(), fetchBrindes(), fetchBrindesPorNivel()])
        .finally(() => setLoading(false));
    }
  }, [niveisDisponiveis, fetchMensagens, fetchBrindes, fetchBrindesPorNivel]);

  useEffect(() => {
    if (!onboardingStepId) return;
    if (onboardingStepId === 'mensagens-primeiro-nivel' && firstNonEpicCheckins !== null) {
      setNiveisAbertos({ [String(firstNonEpicCheckins)]: true });
    }
    if (onboardingStepId === 'mensagens-epico-recorrencia' && epicCheckins !== null) {
      setNiveisAbertos({ [String(epicCheckins)]: true });
    }
  }, [onboardingStepId, firstNonEpicCheckins, epicCheckins]);
  
  if (loading) return <Loader />;

  const handleOpenModal = ({ mensagem = null, ordem = 1, nivelId = "", isRecorrencia } = {}) => {
    setMensagemEditando(mensagem);
    const resolvedRecorrencia = typeof isRecorrencia === 'boolean'
      ? isRecorrencia
      : Boolean(mensagem?.is_recorrencia);
  
    setForm({
      mensagem: mensagem?.mensagem?.replace(/\\n/g, "\n") || "",
      nivel_id: mensagem?.nivel_id || nivelId,
      ordem: mensagem?.ordem || ordem, // ✅ mantém a ordem original se estiver editando
      pergunta_id: mensagem?.pergunta?.id ?? null,
      is_recorrencia: resolvedRecorrencia,
    });
  
    setModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setModalOpen(false);
    setMensagemEditando(null);
  };
  
  const handleSubmit = async () => {
    const endpoint = mensagemEditando
      ? `/fidelidade/mensagens/${mensagemEditando.id}`
      : `/fidelidade/mensagens`;
    const method = mensagemEditando ? "put" : "post";
  
    const payload = buildMensagemPayload(form);
  
    await api[method](endpoint, payload);
  
    await fetchMensagens();
    handleCloseModal();
  };

  const handleDelete = async (id) => {
    try {
      const msgDeletada = mensagens.find((m) => m.id === id);
      if (!msgDeletada) return;

      const nivelId = msgDeletada.nivel_id;
      const isRecorrencia = Boolean(msgDeletada.is_recorrencia);

      await api.delete(`/fidelidade/mensagens/${id}`);

      const mensagensDoNivel = mensagens
        .filter(
          (m) =>
            m.nivel_id === nivelId &&
            m.id !== id &&
            Boolean(m.is_recorrencia) === isRecorrencia
        );

      const updates = buildOrdemUpdatesForGroup(mensagensDoNivel);
      if (updates.length) {
        await applyOrdemUpdates(updates);
      }
      await fetchMensagens();
    } catch (err) {
      console.error("Erro ao deletar ou reordenar mensagens:", err);
      showSnackbar("Erro ao excluir ou reordenar mensagens.", "error");
    }
  };
  
  const handleDeleteNivel = async (nivelId, checkins, isEpic) => {
    if (isEpic) {
      showSnackbar("Nível final não pode ser excluído.", "warning");
      return;
    }
    try {
      await api.delete(`/fidelidade/niveis/${nivelId}`);
  
      setNiveisDisponiveis((prev) => prev.filter((n) => n.id !== nivelId));
      setMensagensNivelTemp((prev) => {
        const novo = { ...prev };
        delete novo[checkins];
        return novo;
      });
  
      showSnackbar("Nível excluído com sucesso!");
    } catch (err) {
      console.error(err);
      showSnackbar("Erro ao excluir nível.", "error");
    }
  };
  
  const toggleNivel = (nivel) => {
    setNiveisAbertos((prev) => (prev[nivel] ? {} : { [nivel]: true }));
  };
  
  const handleDragEnd = (nivel) => async (result) => {
    if (!result.destination) return;
    const mensagensDoNivel = mensagensNivelTemp[nivel] || [];
    const mensagensConversa = mensagensDoNivel.filter((msg) => !msg?.is_recorrencia);
    const mensagensRecorrencia = mensagensDoNivel.filter((msg) => msg?.is_recorrencia);
    const reordered = Array.from(mensagensConversa);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
  
    const reorderedWithOrdem = reordered.map((msg, index) => ({
      ...msg,
      ordem: index + 1,
    }));
  
    setMensagensNivelTemp((prev) => ({
      ...prev,
      [nivel]: [...reorderedWithOrdem, ...mensagensRecorrencia],
    }));
  
    try {
      for (const msg of reorderedWithOrdem) {
        const payload = buildMensagemPayload(msg);
        await api.put(
          `/fidelidade/mensagens/${msg.id}`,
          payload
        );
      }
    } catch (err) {
      console.error("Erro ao salvar nova ordem:", err);
    }
  };  

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Fluxo de Mensagens</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          data-tour="niveis-novo-button"
          onClick={() => {
            setFormNovoNivel({ nome: '', checkins: '', brinde_id: '', recorrencia_checkins: '', is_epic: false });
            setNovoNivelModalOpen(true);
          }}
        >
          Criar Novo Nível
        </Button>
      </Box>
      {Object.entries(mensagensNivelTemp)
        .sort((a, b) => a[0] - b[0])
        .map(([nivel, mensagensDoNivel]) => {
          const nivelInfo = niveisDisponiveis.find((n) => n.checkins === Number(nivel));
          const isEpic = Boolean(nivelInfo?.is_epic);
          const mensagensOrdenadas = [...mensagensDoNivel].sort((a, b) => a.ordem - b.ordem);
          const mensagensConversa = mensagensOrdenadas.filter((msg) => !msg?.is_recorrencia);
          const mensagensRecorrencia = isEpic
            ? mensagensOrdenadas.filter((msg) => msg?.is_recorrencia)
            : [];
          const nextRecorrenciaOrdem = mensagensRecorrencia.length > 0
            ? Math.max(...mensagensRecorrencia.map((msg) => Number(msg.ordem) || 0)) + 1
            : 1;
          const nivelCheckins = Number.isFinite(Number(nivelInfo?.checkins))
            ? Number(nivelInfo.checkins)
            : (Number.isFinite(Number(nivel)) ? Number(nivel) : null);
          const recorrenciaCheckins = Number(nivelInfo?.recorrencia_checkins || 0);
          const hasRecorrencia = recorrenciaCheckins > 0;
          const recurrenceLabel = isEpic && hasRecorrencia
            ? `Recorrência: a cada ${recorrenciaCheckins} check-ins`
            : '';
          const mensagensCount = mensagensOrdenadas.length;
          const brindeKey = nivelInfo?.checkins ?? nivel;
          const brindeId = nivelInfo ? (brindesPorNivelTemp[brindeKey] ?? nivelInfo.brinde_id ?? null) : null;
          const hasBrinde = Boolean(brindeId);
          const entryTitle = 'Conversa (mensagens)';
          const entryDescription = 'Mensagens enviadas automaticamente neste nível.';
          const recurrenceTitle = 'Recorrência no nível final';
          const recurrenceDescription = hasRecorrencia
            ? `Este mesmo fluxo se repete a cada ${recorrenciaCheckins} check-ins.`
            : 'Recorrência não configurada.';
          const recurrenceSendLabel = 'na recorrência do nível final';
          const recurrenceSendTooltip = hasRecorrencia
            ? `Enviada a cada ${recorrenciaCheckins} check-ins após o nível final.`
            : 'Enviada enquanto o cliente permanecer no nível final.';
          const rewardStepNumber = isEpic ? 3 : 2;
          const rewardTitle = isEpic ? 'Recompensa do nível final' : 'Recompensa do nível';
          const rewardDescription = 'Aparece após a conclusão do fluxo de mensagens.';
          const rewardExtraDescription = isEpic
            ? (hasRecorrencia
              ? `Enviada a cada ${recorrenciaCheckins} check-ins enquanto o cliente permanecer no nível final.`
              : 'Enviada enquanto o cliente permanecer no nível final.')
            : '';
          const headerBadgeBase = {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontSize: '0.72rem',
            fontWeight: 600,
            color: '#5a5a5a',
            bgcolor: '#f7f7f7',
            border: '1px solid #e3e3e3',
          };
          const checkinsLabel = nivelCheckins !== null ? nivelCheckins : nivel;
          const nivelCheckinsNumber = Number.isFinite(Number(checkinsLabel))
            ? Number(checkinsLabel)
            : null;
          const isFirstNivel =
            !isEpic &&
            firstNonEpicCheckins !== null &&
            nivelCheckinsNumber === firstNonEpicCheckins;
          const tourProps = isFirstNivel
            ? { 'data-tour': 'nivel-primeiro-card' }
            : isEpic
              ? { 'data-tour': 'nivel-epico-card' }
              : {};
          return (
            <Box
              key={nivel}
              mb={4}
              sx={{ bgcolor: '#FFFAFC', borderRadius: 3, p: 3, boxShadow: 1 }}
              {...tourProps}
            >
              <Grid container alignItems="center" spacing={2} onClick={() => toggleNivel(nivel)} sx={{ cursor: 'pointer' }}>
                {/* Esquerda - Nome do nível */}
                <Grid item xs={12} sm={4} display="flex" alignItems="center" gap={1}>
                  <EmojiEventsIcon color={isEpic ? "warning" : "secondary"} />
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#d30038',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {nivelInfo?.nome || `Nível ${nivel}`}
                      {isEpic && (
                        <Box component="span" sx={{ color: '#000', fontWeight: 600, ml: 1 }}>
                          | Nível Final
                        </Box>
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Ativa a partir de:{' '}
                      <Box component="span" sx={{ fontWeight: 700, color: '#111' }}>
                        {checkinsLabel}
                      </Box>{' '}
                      check-ins
                    </Typography>
                    {isEpic && (
                      <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                        Estado final: não há novos níveis.
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Centro - Indicadores do nível */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Box component="span" sx={headerBadgeBase}>
                      Mensagens: {mensagensCount}
                    </Box>
                    <Box component="span" sx={headerBadgeBase}>
                      {hasBrinde ? 'Com brinde' : 'Sem brinde'}
                    </Box>
                    {recurrenceLabel && (
                      <Box component="span" sx={headerBadgeBase}>
                        {recurrenceLabel}
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Direita - Botões */}
                <Grid item xs={12} sm={4} display="flex" justifyContent="flex-end" gap={1}>
                  {nivelInfo && (
                    <>
                      <Tooltip title="Editar Nível">
                        <IconButton
                          aria-label={`Editar nível ${nivelInfo.nome || ''}`.trim()}
                          onClick={(e) => {
                            e.stopPropagation();
                            const brindeId = brindesPorNivelTemp[nivelInfo.checkins] ?? nivelInfo.brinde_id ?? '';
                            setFormNovoNivel({
                              nome: nivelInfo.nome,
                              checkins: nivelInfo.checkins,
                              brinde_id: brindeId,
                              recorrencia_checkins: nivelInfo.recorrencia_checkins ?? '',
                              is_epic: Boolean(nivelInfo.is_epic),
                              id: nivelInfo.id
                            });
                            setNovoNivelModalOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {!isEpic && (
                        <Tooltip title="Excluir Nível">
                          <IconButton
                            aria-label={`Excluir nível ${nivelInfo.nome || ''}`.trim()}
                            onClick={(e) => {
                              e.stopPropagation();
                              abrirConfirmacao({
                                title: 'Excluir Nível',
                                subtitle: 'Tem certeza que deseja excluir este nível e todas as mensagens vinculadas?',
                                onConfirm: () => handleDeleteNivel(nivelInfo?.id, nivelInfo?.checkins, isEpic)
                              });
                            }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenModal({
                        ordem: mensagensConversa.length + 1,
                        nivelId: nivelInfo?.id || '',
                        isRecorrencia: false,
                      });
                    }}
                  >
                    Nova Mensagem
                  </Button>
                  <IconButton
                    sx={{ mt: 0.5 }}
                    aria-label={niveisAbertos[nivel] ? 'Recolher nível' : 'Expandir nível'}
                  >
                    {niveisAbertos[nivel] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Grid>
              </Grid>
              {niveisAbertos[nivel] && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      mt: 3,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: '#d30038',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      1
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                      {entryTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      — {entryDescription}
                    </Typography>
                  </Box>
                  <Paper elevation={3} sx={{ borderRadius: 2, mb: 3, mt: 2 }}> 
                    <Box
                      sx={{
                        backgroundImage: 'url(/background3.png)',
                        backgroundRepeat: 'repeat',
                        backgroundSize: '200px', // ou outro valor como '80px' ou '10%'
                        backgroundPosition: 'top left',

                        borderRadius: 3,
                        px: { xs: 2, sm: 4 },
                        py: { xs: 2, sm: 4 },
                        mb: 3,
                      }}
                    >
                      <DragDropContext onDragEnd={handleDragEnd(nivel)}>
                        <Droppable droppableId={`nivel-${nivel}`}>
                          {(provided) => (
                            <List
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 3,
                              }}
                            >
                              {mensagensConversa.map((item, index) => {
                                const previousMessage = mensagensConversa[index - 1];
                                const hasPrevQuestion = Boolean(previousMessage?.pergunta_id || previousMessage?.pergunta);
                                let sendLabel = 'sequência do fluxo';
                                let sendTooltip = 'Enviada em sequência após a mensagem anterior.';

                                if (index === 0) {
                                  const hasNivelCheckins = nivelCheckins !== null;
                                  if (isEpic) {
                                    sendLabel = 'entrada no nível final';
                                    sendTooltip = hasNivelCheckins
                                      ? `Enviada quando o cliente entra no nível final pela primeira vez ao completar ${nivelCheckins} check-ins.`
                                      : 'Enviada quando o cliente entra no nível final pela primeira vez.';
                                  } else {
                                    sendLabel = hasNivelCheckins
                                      ? 'no check-in do nível'
                                      : 'no check-in do fluxo';
                                    sendTooltip = hasNivelCheckins
                                      ? `Enviada quando o cliente completa ${nivelCheckins} check-ins.`
                                      : 'Enviada no check-in que inicia este fluxo.';
                                  }
                                } else if (hasPrevQuestion) {
                                  sendLabel = 'após resposta do cliente';
                                  sendTooltip = 'Enviada quando o cliente responde a mensagem anterior.';
                                }

                                return (
                                  <Draggable key={item.id} draggableId={`${item.id}`} index={index}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={{
                                          marginBottom: '24px',
                                          width: '100%',
                                          maxWidth: '600px',
                                          ...provided.draggableProps.style,
                                        }}
                                      >
                                        <MensagemCard
                                          item={item}
                                          dragHandleProps={provided.dragHandleProps}
                                          onEdit={() => handleOpenModal({ mensagem: item })}
                                          onDelete={() =>
                                            abrirConfirmacao({
                                              title: 'Excluir Mensagem',
                                              subtitle: 'Tem certeza que deseja excluir esta mensagem?',
                                              onConfirm: () => handleDelete(item.id),
                                            })
                                          }
                                          flowInfo={{
                                            sendLabel,
                                            sendTooltip,
                                          }}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </List>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </Box>
                  </Paper>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Fluxo de mensagens concluído
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>

                  {isEpic ? (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: '#d30038',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          2
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                          {recurrenceTitle}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Enquanto o cliente permanecer no nível final.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: '#fff6f9',
                          borderRadius: 2,
                          border: '1px dashed #f2d2dd',
                          px: { xs: 2, sm: 3 },
                          py: { xs: 2, sm: 2.5 },
                          mt: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {recurrenceDescription}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          mt: 2,
                        }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenModal({
                            ordem: nextRecorrenciaOrdem,
                            nivelId: nivelInfo?.id || '',
                            isRecorrencia: true,
                          })}
                        >
                          Nova Mensagem Recorrente
                        </Button>
                      </Box>
                      {mensagensRecorrencia.length > 0 ? (
                        <Paper elevation={3} sx={{ borderRadius: 2, mb: 3, mt: 2 }}>
                          <Box
                            sx={{
                              backgroundImage: 'url(/background3.png)',
                              backgroundRepeat: 'repeat',
                              backgroundSize: '200px',
                              backgroundPosition: 'top left',
                              borderRadius: 3,
                              px: { xs: 2, sm: 4 },
                              py: { xs: 2, sm: 4 },
                              mb: 3,
                            }}
                          >
                            <List
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 3,
                              }}
                            >
                              {mensagensRecorrencia.map((item) => (
                                <Box
                                  key={item.id}
                                  sx={{
                                    width: '100%',
                                    maxWidth: '600px',
                                    mb: 3,
                                  }}
                                >
                                  <MensagemCard
                                    item={item}
                                    onEdit={() => handleOpenModal({ mensagem: item })}
                                    onDelete={() =>
                                      abrirConfirmacao({
                                        title: 'Excluir Mensagem',
                                        subtitle: 'Tem certeza que deseja excluir esta mensagem?',
                                        onConfirm: () => handleDelete(item.id),
                                      })
                                    }
                                    flowInfo={{
                                      sendLabel: recurrenceSendLabel,
                                      sendTooltip: recurrenceSendTooltip,
                                    }}
                                  />
                                </Box>
                              ))}
                            </List>
                          </Box>
                        </Paper>
                      ) : (
                        <Box
                          sx={{
                            backgroundColor: '#fff6f9',
                            borderRadius: 2,
                            border: '1px dashed #f2d2dd',
                            px: { xs: 2, sm: 3 },
                            py: { xs: 2, sm: 2.5 },
                            mt: 2,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Nenhuma mensagem de recorrência cadastrada.
                          </Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 3 }} />
                    </>
                  ) : (
                    <Divider sx={{ my: 3 }} />
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: '#d30038',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {rewardStepNumber}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                      {rewardTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      — {rewardDescription}
                    </Typography>
                  </Box>
                  {rewardExtraDescription && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      {rewardExtraDescription}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      backgroundColor: '#fff6f9',
                      borderRadius: 2,
                      border: '1px solid #f2d2dd',
                      px: { xs: 2, sm: 3 },
                      py: { xs: 2, sm: 2.5 },
                    }}
                  >
                    <BrindePorNivelSection
                      nivel={nivel}
                      brindes={brindes}
                      brindesPorNivelTemp={brindesPorNivelTemp}
                      setBrindesPorNivelTemp={setBrindesPorNivelTemp}
                      salvarBrindePorNivel={salvarBrindePorNivel}
                    />
                  </Box>
                </>
              )}
            </Box>
          );
        })
      }
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
          {mensagemEditando ? 'Editar Mensagem' : 'Nova Mensagem'}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
            <TextField
              select
              label="Atributo a coletar (opcional)"
              fullWidth
              value={form.pergunta_id ?? ''}
              helperText={'Se você selecionar um atributo, o sistema vai pausar o fluxo e aguardar a resposta do cliente antes de enviar a próxima mensagem.\nSe você não selecionar, o fluxo continua automaticamente.'}
              FormHelperTextProps={{ sx: { whiteSpace: 'pre-line' } }}
              onChange={(e) => {
                const selectedValue = e.target.value === '' ? null : Number(e.target.value);
                const perguntaSelecionada = perguntas.find(p => p.id === selectedValue);

                setForm((prev) => ({
                  ...prev,
                  pergunta_id: selectedValue,
                  mensagem: perguntaSelecionada?.msg_padrao || prev.mensagem
                }));
              }}
            >
              <MenuItem value="">Nenhum atributo</MenuItem>
              {perguntas.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.descricao}
                </MenuItem>
              ))}
            </TextField>

            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Mensagem"
                fullWidth
                value={form.mensagem}
                onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                multiline
                minRows={3}
                helperText="Conteúdo livre. Este texto será enviado ao cliente automaticamente conforme o disparo configurado."
              />
            </Grid> 
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ fontWeight: 'bold' }}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={novoNivelModalOpen}
        onClose={() => setNovoNivelModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 'data-tour': 'niveis-modal-paper' }}
      >
        <DialogTitle>{formNovoNivel.id ? 'Editar Nível' : 'Criar Novo Nível'}</DialogTitle>
        <DialogContent dividers data-tour="niveis-modal-content">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nome do Nível"
                fullWidth
                value={formNovoNivel.nome}
                onChange={(e) => setFormNovoNivel({ ...formNovoNivel, nome: e.target.value })}
                helperText="Este nome aparece no painel e pode ser qualquer nome definido pelo restaurante."
              />
            </Grid>
            <Grid item xs={12}>
            <TextField
              label="Número de Check-ins"
              type="number"
              fullWidth
              value={formNovoNivel.checkins}
              onChange={(e) => setFormNovoNivel({ ...formNovoNivel, checkins: e.target.value })}
              error={erroCheckinsDuplicado}
              helperText={erroCheckinsDuplicado ? 'Este número de check-ins já está em uso.' : 'O cliente entra neste nível quando atingir pelo menos este número de check-ins.'}
            />
            </Grid>

            {formNovoNivel.is_epic && (
              <Grid item xs={12}>
                <TextField
                  label="Recorrência de check-ins"
                  type="number"
                  fullWidth
                  value={formNovoNivel.recorrencia_checkins}
                  onChange={(e) => setFormNovoNivel({ ...formNovoNivel, recorrencia_checkins: e.target.value })}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                select
                label="Brinde (opcional)"
                fullWidth
                value={formNovoNivel.brinde_id}
                onChange={(e) => setFormNovoNivel({ ...formNovoNivel, brinde_id: e.target.value })}
                helperText="Se definido, o brinde é liberado após a conclusão do fluxo de mensagens deste nível."
              >
                <MenuItem value="">Nenhum</MenuItem>
                {brindes.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNovoNivelModalOpen(false)}>Cancelar</Button>
          <Button
            onClick={async () => {
              try {
                const checkinJaExiste = niveisDisponiveis.some(n =>
                  Number(n.checkins) === Number(formNovoNivel.checkins) &&
                  n.id !== formNovoNivel.id
                );

                if (checkinJaExiste) {
                  showSnackbar(`Já existe um nível com ${formNovoNivel.checkins} check-in(s). Escolha outro valor.`, 'warning');
                  setErroCheckinsDuplicado(true);
                  return;
                }
                setErroCheckinsDuplicado(false);
                


                const nivelPayload = {
                  nome: formNovoNivel.nome,
                  checkins: formNovoNivel.checkins,
                  brinde_id: formNovoNivel.brinde_id || null
                };

                if (formNovoNivel.is_epic) {
                  nivelPayload.recorrencia_checkins = formNovoNivel.recorrencia_checkins || null;
                }

                if (formNovoNivel.id) {
                  await api.put(`/fidelidade/niveis/${formNovoNivel.id}`, nivelPayload);
                } else {
                  await api.post('/fidelidade/niveis', nivelPayload);
                }

                showSnackbar('Nível salvo com sucesso!');
                setNovoNivelModalOpen(false);
                setFormNovoNivel({ nome: '', checkins: '', brinde_id: '', recorrencia_checkins: '', is_epic: false });
                await fetchNiveis();
                await fetchBrindesPorNivel();
              } catch (err) {
                const apiMessage = err?.response?.data?.error;
                showSnackbar(apiMessage || 'Erro ao salvar nível.', 'error');
                console.error(err);
              }
            }}
            variant="contained"
          >
            {formNovoNivel.id ? 'Atualizar' : 'Salvar'}
          </Button>

        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: '#FFFAFC',
            boxShadow: 3,
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 'bold',
            color: '#d30038'
          }}
        >
          <WarningAmberRoundedIcon sx={{ color: '#d30038' }} />
          {confirmDialog.title}
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {confirmDialog.subtitle}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false }); // FECHA PRIMEIRO
              setTimeout(() => {
                confirmDialog.onConfirm(); // EXECUTA A AÇÃO DEPOIS
              }, 100);
            }}
            sx={{
              backgroundColor: '#d30038',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#b00030',
              }
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MensagensTab;
